import React from "react";
import { useTransition, animated } from "@react-spring/web";

interface InfoModalProps {
  isOpen: boolean;
  onClose: () => void;
}

const InfoModal: React.FC<InfoModalProps> = ({ isOpen, onClose }) => {
  const transitions = useTransition(isOpen, {
    from: { opacity: 0 },
    enter: { opacity: 1 },
    leave: { opacity: 0 },
    config: { duration: 300 },
  });

  return transitions(
    (style, item) =>
      item && (
        <animated.div
          style={style}
          className="fixed top-0 left-0 w-full h-screen px-2 absolute flex items-center justify-center z-20"
        >
          <div
            className="
            relative
            p-6
            border-2
            border-gray-300
            rounded
            text-white
            flex
            flex-col
            backdrop-blur
            max-w-full
            md:max-w-lg
            h-[450px]
            bg-black/60
          "
          >
            <button
              className="
              absolute
              bg-black
              p-2
              right-0
              top-0
              -translate-y-1/2
              translate-x-1/2
            "
              onClick={onClose}
            >
              {/* Replace with an actual close icon or X */}
              <span className="d-icon flex d-close"></span>
            </button>

            <div className="overflow-y-auto hide-scrollbars h-full pr-2">
              <h2 className="text-xl text-center font-semibold mb-3">
                DISCLAIMER
              </h2>

              <div className="space-y-3">
                <section>
                  <h3 className="font-italic mb-2">
                    Inhoudelijke verantwoording
                  </h3>
                  <p>
                    De inhoud van deze website geeft een kijkje in hoe het
                    museum kiest de collectie te presenteren, ingegeven door de
                    tijd waarin we leven. De inhoud is hierdoor aan verandering
                    onderhevig. De inhoud van deze website wordt wekelijks
                    geactualiseerd. Aan de inhoud kunnen geen rechten worden
                    ontleend. Alle informatie is onder voorbehoud van
                    wijzigingen.
                  </p>
                </section>

                <section>
                  <h3 className="font-italic mb-2">Auteursrecht</h3>
                  <p>Het auteursrecht op deze site berust bij Museum Arnhem.</p>
                </section>

                <section>
                  <h3 className="font-italic mb-2">Beeldrecht</h3>
                  <p>
                    Afbeeldingen op deze website mogen niet voor commerciële
                    doeleinden gebruikt of gedupliceerd worden. Op sommige
                    afbeeldingen kan beeldrecht rusten, dit staat vermeld bij de
                    afbeelding. Wil je afbeeldingen gebruiken van makers die nog
                    geen 70 jaar geleden overleden zijn? Neem dan contact op met
                    Pictoright, de auteursrechtorganisatie voor visuele makers
                    in Nederland.
                  </p>
                </section>

                <section>
                  <h3 className="font-italic mb-2">
                    Mede mogelijk gemaakt door
                  </h3>
                  <p>
                    Collectie Connectie is een samenwerking tussen Museum Arnhem
                    en <span className="font-bold">SPRIGS RND</span> (
                    <a
                      target="_blank"
                      href="https://sprigsrnd.com"
                      className="underline"
                      rel="noopener noreferrer"
                    >
                      sprigsrnd.com
                    </a>
                    ).
                  </p>
                </section>

                <p className="text-sm">
                  © Museum Arnhem, Arnhem {new Date().getFullYear()}
                </p>

                <div className="bg-white flex justify-around items-center gap-3 p-4 mb-4">
                  <img
                    className="w-12 md:w-20"
                    src={"/images/logo-1.png"}
                    alt="Mondriaan Fund"
                  />
                  <img
                    className="w-12 md:w-20"
                    src={"/images/logo-2.svg"}
                    alt="provincie GELDERLAND"
                  />
                  <img
                    className="w-12 md:w-20"
                    src={"/images/logo-3.png"}
                    alt="Prins Bernhard Cultuurfonds"
                  />
                </div>

                {/* Cookies Policy Section */}
                <section>
                  <h4 className="mb-1 font-bold">COOKIES POLICY</h4>
                  <p>
                    Museum Arnhem ("ons", "wij", of "onze") maakt gebruik van
                    cookies op
                    <a
                      target="_blank"
                      href="https://collectieconnectie.nl"
                      className="underline"
                      rel="noreferrer"
                    >
                      https://collectieconnectie.nl
                    </a>
                    (de "Service"). Wanneer u gebruik maakt van de Service, gaat
                    u akkoord met het gebruik van cookies. Onze cookies policy
                    verduidelijkt wat cookies zijn, hoe we cookies gebruiken,
                    hoe derde partijen waarmee we partnerships sluiten cookies
                    kunnen gebruiken op de Service, uw keuzes wat betreft
                    cookies en meer informatie over cookies.
                  </p>
                  <p className="mb-1 font-bold">Wat zijn cookies</p>
                  <p className="mb-3">
                    Cookies zijn kleine tekstbestandjes die naar uw browser
                    verstuurd worden door een website die u bezoekt. Een
                    cookiebestand wordt opgeslagen in uw webbrowser en laat de
                    Service of een derde partij toe u te herkennen om uw
                    volgende bezoek makkelijker te maken en de Service meer op u
                    af te stemmen. Cookies kunnen "permanent" of "tijdelijk"
                    zijn. Permanente cookies blijven op uw pc of mobiel toestel
                    wanneer u offline gaat, terwijl tijdelijke cookies meteen
                    verwijderd worden als u uw browser afsluit.
                  </p>
                  <p className="mb-1 font-bold">Hoe gebruiken wij cookies</p>
                  <p className="mb-3">
                    Wanneer u gebruik maakt van en toegang krijgt tot de
                    Service, kunnen we een aantal cookiebestanden in uw
                    webbrowser plaatsen. We gebruiken cookies voor volgende
                    doeleinden: om bepaalde functies van de service mogelijk te
                    maken, voor analyse en om uw voorkeuren op te slaan.
                    <br />
                    We maken zowel gebruik van tijdelijke als permanente cookies
                    op de Service. Cookies zullen niet gebruikt worden voor
                    andere doeleinden dan diegene vermeld.
                  </p>

                  <p className="mb-1 font-bold">Permanente cookies</p>
                  <p>We gebruiken permanente cookies op de Service:</p>
                  <ul className="mb-3">
                    <li>
                      Cookies van derden. Boven op onze eigen cookies, kunnen we
                      ook gebruik maken van verscheidene cookies van derden om
                      bepaalde functies van de Service mogelijk te maken.
                    </li>
                  </ul>

                  <p className="mb-1 font-bold">Tijdelijke cookies</p>
                  <p>We gebruiken tijdelijke cookies op de Service:</p>
                  <ul className="mb-3">
                    <li>
                      Essentiële cookies. We kunnen gebruik maken van essentiële
                      cookies om gebruikersacties te onthouden.
                    </li>
                    <li>
                      Marketing cookies. We kunnen gebruik maken van marketing
                      (analytische) cookies om bij te houden welke pagina’s
                      bezocht worden op de Service.
                    </li>
                  </ul>

                  <p className="mb-1 font-bold">
                    Welke keuzes heeft u met betrekking tot cookies
                  </p>
                  <p>
                    Als u cookies wenst te verwijderen of uw webbrowser de
                    opdracht wilt geven om cookies te verwijderen of te
                    weigeren, gaat u naar de helppagina’s van uw browser. Let
                    wel op dat indien u cookies verwijdert of weigert ze te
                    accepteren, u misschien niet alle aangeboden
                    functionaliteiten zal kunnen gebruiken, u uw voorkeuren
                    misschien niet zal kunnen opslaan en sommige van onze
                    pagina’s misschien niet correct getoond zullen worden.:
                  </p>

                  <ul className="mb-3">
                    <li>
                      Voor de Chrome webbrowser, gaat u naar deze pagina van
                      Google:
                      <a
                        target="_blank"
                        className="underline"
                        href="https://support.google.com/accounts/answer/32050"
                        rel="noreferrer"
                      >
                        https://support.google.com/accounts/answer/32050
                      </a>
                    </li>
                    <li>
                      Voor de Internet Explorer webbrowser, gaat u naar deze
                      pagina van Microsoft:
                      <a
                        target="_blank"
                        className="underline"
                        href="http://support.microsoft.com/kb/278835"
                        rel="noreferrer"
                      >
                        http://support.microsoft.com/kb/278835
                      </a>
                    </li>
                    <li>
                      Voor de Firefox webbrowser, gaat u naar deze pagina van
                      Mozilla:
                      <a
                        target="_blank"
                        className="underline"
                        href="https://support.mozilla.org/en-US/kb/delete-cookies-remove-info-websites-stored"
                        rel="noreferrer"
                      >
                        https://support.mozilla.org/en-US/kb/delete-cookies-remove-info-websites-stored
                      </a>
                    </li>
                    <li>
                      Voor de Safari webbrowser, gaat u naar deze pagina van
                      Apple:
                      <a
                        target="_blank"
                        className="underline"
                        href="https://support.apple.com/kb/PH21411?locale=en_US"
                        rel="noreferrer"
                      >
                        https://support.apple.com/kb/PH21411?locale=en_US
                      </a>
                    </li>
                    <li>
                      Voor elke andere webbrowser, gaat u naar de officiële
                      webpagina’s van uw webbrowser.
                    </li>
                  </ul>

                  <p className="mb-1 font-bold">
                    Waar kan u meer informatie vinden over cookies
                  </p>
                  <p>
                    U kan meer info vinden over cookies op de volgende website
                    van derden: AllAboutCookies{" "}
                    <a
                      target="_blank"
                      className="underline"
                      href="http://wwww.allaboutcookies.org"
                      rel="noreferrer"
                    >
                      http://wwww.allaboutcookies.org
                    </a>
                  </p>
                </section>
              </div>
            </div>
          </div>
        </animated.div>
      ),
  );
};

export default InfoModal;
